import React from 'react';
import Header from '../component/Header';
import Footer from '../component/footer';
import Seo from '../components/Seo';

const PrivacyPolicy = () => {
  return (
    <>
      <Seo
        title="Privacy Policy | LearnWebAcademy"
        description="Learn how personal information is collected, used, and protected to ensure privacy, security, and transparency across the platform."
        canonicalPath="/privacy"
      />
      <Header />

      <div className="min-h-screen flex flex-col bg-[#F4F5F7] font-sans text-gray-800">

      <main className="flex-grow w-full max-w-[1280px] mx-auto px-4 md:px-8 py-12 md:py-20">
        
        {/* Content Card */}
        <div className="bg-white rounded-[2.5rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 p-8 md:p-12 lg:p-16">
          
          {/* Page Header */}
          <div className="mb-12">
            <h1 className="text-3xl md:text-5xl font-extrabold text-[#113A28] tracking-tight mb-6">
              Privacy Policy
            </h1>
            <div className="w-16 h-1.5 bg-[#4CE470] rounded-full mb-8"></div>
            <p className="text-sm font-bold tracking-widest text-gray-400 uppercase">
              Last Updated: March 2026
            </p>
          </div>

          <hr className="mb-12 border-gray-100" />

          {/* Policy Content */}
          <div className="grid grid-cols-1 gap-12 leading-relaxed text-gray-600">
            
            <section>
              <h2 className="text-xl md:text-2xl font-bold text-[#113A28] mb-4">1. Consent to Privacy Policy</h2>
              <p>
                By your continued use of the merchant’s services following the posting of this Privacy Policy, you agree to this Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-[#113A28] mb-4">2. Information Collection and Personally Identifiable Information</h2>
              <p className="mb-4">We collect the following personal data:</p>
              <ul className="pl-6 mb-6 space-y-2 list-disc">
                <li><strong className="text-[#113A28]">Registration data:</strong> email address, password, first name, last name, phone number, and payment details.</li>
                <li><strong className="text-[#113A28]">Usage data:</strong> information about your activity when using our services.</li>
                <li><strong className="text-[#113A28]">Marketing data:</strong> email address for communication of offers, newsletters, or updates.</li>
              </ul>
              <p className="mb-6">
                We use this information to create and manage your account, contact you, enable payments, prevent fraud, customize services, and improve your experience.
              </p>
              
              <div className="p-6 mb-6 border border-gray-100 bg-gray-50 rounded-2xl">
                <h3 className="font-bold text-[#113A28] mb-3">Legal basis:</h3>
                <ul className="pl-6 space-y-1 list-disc">
                  <li>Contract performance (to provide services).</li>
                  <li>Legitimate interests (direct marketing and service improvement).</li>
                  <li>Compliance with legal obligations (e.g., accounting, tax).</li>
                </ul>
              </div>

              <h3 className="font-bold text-[#113A28] mb-3">Your rights:</h3>
              <p className="mb-4">
                You may request correction, deletion, access, data portability, restriction of processing, or object to processing (especially marketing). We will honor such requests unless an overriding legal obligation applies.
              </p>
              <p>
                We entrust processing to GDPR-compliant data processors, such as hosting providers and payment processors. We may also be legally required to disclose information to authorities (e.g., police or courts).
              </p>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-[#113A28] mb-4">3. Information Use</h2>
              <ul className="pl-6 space-y-2 list-disc">
                <li>Processing payments securely.</li>
                <li>Customizing services, ads, and recommendations.</li>
                <li>Contacting you and responding to requests.</li>
                <li>Conducting research and improving services.</li>
                <li>Preventing fraud and ensuring security.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-[#113A28] mb-4">4. Information Sharing and Disclosure</h2>
              <p className="mb-4">We will not disclose your personally identifiable information except:</p>
              <ul className="pl-6 mb-4 space-y-2 list-disc">
                <li>Where required by law.</li>
                <li>For service provision (e.g., hosting, payment processing).</li>
                <li>In connection with corporate transactions (e.g., merger or sale).</li>
                <li>With carefully selected contractors bound by confidentiality.</li>
              </ul>
              <p>
                We may share aggregate, anonymous information with partners, advertisers, or content distributors.
              </p>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-[#113A28] mb-4">5. Changing or Deleting Your Information</h2>
              <p>
                You may review, update, correct, or delete your personal information by contacting us. Deletion may deactivate your account. We may retain archived data where legally required.
              </p>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-[#113A28] mb-4">6. Use of Cookies and Similar Technologies</h2>
              <p className="mb-4">
                The merchant uses cookies, web beacons, device fingerprinting, and similar technologies for functionality, analytics, personalization, and advertising.
              </p>
              <h3 className="font-bold text-[#113A28] mb-3">Types of cookies used include:</h3>
              <ul className="grid grid-cols-1 gap-2 mb-6 md:grid-cols-2">
                <li className="px-4 py-2 border border-gray-100 rounded-lg bg-gray-50">Persistent and session cookies</li>
                <li className="px-4 py-2 border border-gray-100 rounded-lg bg-gray-50">Strictly necessary cookies</li>
                <li className="px-4 py-2 border border-gray-100 rounded-lg bg-gray-50">Performance cookies</li>
                <li className="px-4 py-2 border border-gray-100 rounded-lg bg-gray-50">Functionality cookies</li>
                <li className="px-4 py-2 border border-gray-100 rounded-lg bg-gray-50">Targeting/advertising cookies</li>
              </ul>
              <p className="mb-4">
                Cookies help improve navigation, remember preferences, and display relevant ads. You can manage cookies in your browser settings. Blocking cookies may affect site functionality.
              </p>
              <p>
                We also use Google Analytics and may link cookies to advertising platforms such as Facebook.
              </p>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-[#113A28] mb-4">7. Behavioral Advertising and Online Privacy</h2>
              <p className="mb-4">
                We may use cookies and online identifiers for behavioral advertising, in line with industry standards (see <a href="https://youronlinechoices.eu" className="text-[#4CE470] font-bold hover:underline">youronlinechoices.eu</a>).
              </p>
              <p>
                You can manage cookies via browser settings, but some site functions may not work if cookies are disabled.
              </p>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-[#113A28] mb-4">8. Changes and Updates</h2>
              <p>
                We may periodically update this Privacy Policy and will notify you by posting the modified version on the Website or contacting you by email.
              </p>
            </section>

  <section>
              <h2 className="text-xl md:text-2xl font-bold text-[#113A28] mb-4">9. Contact</h2>
              <p>
                 Questions, comments, and requests regarding this Privacy Policy are welcomed and should be addressed to the merchant’s support team.
              </p>
            </section>
           

          </div>
        </div>
      </main>

   
    </div>
       <Footer />
    </>
  );
};

export default PrivacyPolicy;           
